<template>
  <div
    ref="menuTrigger"
    :class="['menu-trigger', { 'menu-trigger--children': hasChildren }]"
  >
    <slot />
    <div
      v-if="hasChildren"
      class="menu-trigger__arrow menu-trigger__arrow--no-pointer"
    >
      <svg
        aria-hidden="true"
        :class="['icon arrow-down', { 'arrow-down--turn': active }]"
        role="img"
      >
        <use xlink:href="#arrow-down" />
      </svg>
    </div>
  </div>
</template>

<script>
  import { throttle } from '@Foundation/utilities';
  import { mapActions, mapGetters } from 'vuex';
  import { HEADER_MOBILE } from '@Foundation/constants/headermobile';
  export default {
    name: 'MenuTrigger',
    props: {
      menuId: { type: String, required: true },
      isLink: { type: Boolean, required: true },
      hasChildren: { type: Boolean, required: false, default: false },
    },
    data() {
      return {
        button: null,
        active: false,
        smallScreen: !window.matchMedia(`(min-width: ${HEADER_MOBILE}px)`).matches,
      };
    },
    computed: {
      ...mapGetters('menuStore', ['activeMenuId', 'menuElements']),
      isNotActive() {
        return !this.activeMenuId || this.activeMenuId !== this.menuId;
      },
    },
    watch: {
      activeMenuId(newId) {
        return newId === this.menuId ? this.updateMenu() : this.clearMenu();
      },
    },
    mounted() {
      this.button = this.$refs.menuTrigger.querySelector('.btn--menu');

      this.addMenuElementToList(this.button);
      this.bindEventsToButton();
      this.setAriaHasPopup();
      this.toggleKeyEvents();

      /* istanbul ignore next */
      window.addEventListener(
        'resize',
        throttle(() => {
          this.smallScreen = !window.matchMedia(`(min-width: ${HEADER_MOBILE}px)`).matches;
          this.bindEventsToButton();
          this.toggleKeyEvents();
        }, 100)
      );
    },
    beforeDestroy() {
      this.$refs.menuTrigger.removeEventListener('keydown', this.keyDownEvents);
    },
    methods: {
      ...mapActions('menuStore', ['setActiveMenuId', 'setActiveBtnElem', 'addMenuElementToList']),
      keyDownEvents(e) {
        if (document.activeElement.id !== this.button.id) {
          return;
        }
        switch (e.keyCode) {
          case 39:
            this.focusNextBtn(this.menuElements, document.activeElement);
            break;
          case 37:
            this.focusPreviousBtn(this.menuElements, document.activeElement);
            break;
          case 27 || 32 || 13:
            this.toggleMenu();
            break;
          default:
            break;
        }
      },
      bindEventsToButton() {
        this.button.removeEventListener('mouseover', this.toggleMenu);
        this.button.removeEventListener('focus', this.toggleMenu);
        this.button.removeEventListener('click', this.toggleMenu);

        if (this.smallScreen) {
          this.button.addEventListener('click', this.toggleMenu);
        } else {
          this.button.addEventListener('mouseover', this.toggleMenu);
          this.button.removeEventListener('touchstart', this.toggleMenu);
          this.button.addEventListener('focus', this.toggleMenu);
          this.button.addEventListener('mouseleave', event => {
            if (
              event.relatedTarget.classList &&
              !event.relatedTarget.classList.contains('menu-drawer')
            ) {
              this.active = false;
              this.setActiveMenuId(null);
            }
          });
        }
      },
      toggleMenu() {
        this.setActiveMenuId(this.isNotActive ? this.menuId : null);
      },
      updateMenu() {
        this.active = !this.active;
        this.setAriaExpanded(this.active);
        this.toggleBtnClass();
      },
      clearMenu() {
        this.active = false;
        this.setAriaExpanded(this.active);
        this.removeBtnClass();
      },
      focusNextBtn(list, current) {
        const next = list[list.indexOf(current) + 1];
        switch (next) {
          case list[0]:
            return list[list.indexOf(current) + 2].focus();
          case list[list.length]:
            return list[0].focus();
          default:
            return next ? next.focus() : null;
        }
      },
      focusPreviousBtn(list, current) {
        return list.indexOf(current) === 0
          ? list[list.length - 1].focus()
          : list[list.indexOf(current) - 1].focus();
      },
      toggleBtnClass() {
        this.button.classList.toggle('btn--menu--active');
      },
      removeBtnClass() {
        this.button.classList.remove('btn--menu--active');
      },
      setAriaExpanded(status) {
        if (this.button.tagName !== 'BUTTON') {
          return;
        }
        this.button.setAttribute('aria-expanded', status);
      },
      setAriaHasPopup() {
        if (this.smallScreen) {
          this.button.setAttribute('aria-haspopup', false);
          return;
        }
        if (this.button.tagName !== 'BUTTON') {
          return;
        }
        // Client asked for this, not 100% sure but will remove for now
        // this.button.setAttribute('aria-haspopup', true);
      },
      toggleKeyEvents() {
        if (!this.$refs.menuTrigger) {
          return;
        }
        return !this.smallScreen
          ? this.$refs.menuTrigger.addEventListener('keydown', this.keyDownEvents)
          : this.$refs.menuTrigger.removeEventListener('keydown', this.keyDownEvents);
      },
    },
  };
</script>
